/**
 * Recommendation Detail Screen Component
 *
 * Main screen for viewing detailed information about a recommendation,
 * including exit fund analysis, replacement fund details, cost breakdown,
 * and side-by-side comparison.
 *
 * Features:
 * - Header with recommendation type, priority, and action buttons
 * - Exit fund analysis section (current holding details, why exit is recommended)
 * - Replacement fund details with score breakdown
 * - Side-by-side comparison table
 * - Switch cost breakdown
 * - Tax impact summary
 */

import { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatPercent, formatDate } from "@/lib/utils";
import {
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  Bookmark,
  Play,
  X,
  Info,
  Wallet,
  Calendar,
  Target,
} from "lucide-react";
import type { FundRating, FundIsin, HoldingId, TaxImpact } from "@curie/shared";

// Import API client
import { api, unwrapResponse } from "@/lib/api";

// Import components
import {
  ScoreBreakdownBars,
  ScoreBreakdownBarsSkeleton,
} from "@/components/holding-detail/ScoreBreakdownBars";
import { CopyISINButton } from "@/components/actions/CopyISINButton";
import { WatchlistButton } from "@/components/actions/WatchlistButton";
import { ExportAnalysisButton } from "@/components/actions/ExportAnalysisButton";
import { SwitchCostTable, SwitchCostTableSkeleton } from "./SwitchCostTable";
import {
  FundComparisonTable,
  FundComparisonTableSkeleton,
} from "./FundComparisonTable";

// ============================================================================
// Types
// ============================================================================

/**
 * Recommendation detail data from API
 */
export interface RecommendationDetailData {
  id: string;
  type: "switch" | "exit" | "increase-sip" | "start-sip";
  priority: "high" | "medium" | "low";
  reason: string;
  impactDescription: string;
  createdAt: string;

  exitFund?: {
    isin: FundIsin;
    name: string;
    amcName: string;
    category: string;
    rating: FundRating;
    rank: number;
    totalFundsInCategory: number;
    nav: number;
    returns1Y: number;
    returns3Y: number;
    expenseRatio: number;
    consistencyScore: number;
    holding?: {
      id: HoldingId;
      units: number;
      investedAmount: number;
      currentValue: number;
      totalReturns: number;
      totalReturnsPercent: number;
      xirr: number;
      firstInvestmentDate: string;
    };
  };

  replacementFund?: {
    isin: FundIsin;
    name: string;
    amcName: string;
    category: string;
    rating: FundRating;
    rank: number;
    totalFundsInCategory: number;
    nav: number;
    returns1Y: number;
    returns3Y: number;
    expenseRatio: number;
    consistencyScore: number;
    benchmarkIndex: string;
    sharpeRatio: number;
    alpha: number;
  };

  switchCost?: {
    exitLoad: number;
    stt: number;
    stampDuty: number;
    totalCost: number;
    costPercentage: number;
    notes: string[];
  };

  taxImpact?: TaxImpact;

  comparison?: {
    returnsDifference1Y: number;
    returnsDifference3Y: number;
    expenseRatioDifference: number;
    rankImprovement: number;
    consistencyScoreDifference: number;
    projectedBenefit: string;
  };
}

/**
 * Props for the RecommendationDetailScreen component
 */
export interface RecommendationDetailScreenProps {
  /** The recommendation ID to display */
  recommendationId: string;
  /** Callback when back button is pressed */
  onBack?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Recommendation type labels and icons
 */
const TYPE_CONFIG: Record<
  RecommendationDetailData["type"],
  { label: string; icon: typeof ArrowRightLeft; color: string }
> = {
  switch: {
    label: "Switch Recommendation",
    icon: ArrowRightLeft,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  },
  exit: {
    label: "Exit Recommendation",
    icon: TrendingDown,
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  "increase-sip": {
    label: "Increase SIP",
    icon: TrendingUp,
    color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  "start-sip": {
    label: "Start SIP",
    icon: Play,
    color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  },
};

/**
 * Priority colors
 */
const PRIORITY_CONFIG: Record<
  RecommendationDetailData["priority"],
  { label: string; color: string }
> = {
  high: {
    label: "High Priority",
    color: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  },
  medium: {
    label: "Medium Priority",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  low: {
    label: "Low Priority",
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
  },
};

/**
 * Rating configuration
 */
const RATING_CONFIG: Record<
  FundRating,
  { label: string; bgColor: string; textColor: string }
> = {
  "in-form": {
    label: "In-Form",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    textColor: "text-emerald-700 dark:text-emerald-400",
  },
  "on-track": {
    label: "On-Track",
    bgColor: "bg-sky-100 dark:bg-sky-900/30",
    textColor: "text-sky-700 dark:text-sky-400",
  },
  "off-track": {
    label: "Off-Track",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    textColor: "text-amber-700 dark:text-amber-400",
  },
  "out-of-form": {
    label: "Out-of-Form",
    bgColor: "bg-rose-100 dark:bg-rose-900/30",
    textColor: "text-rose-700 dark:text-rose-400",
  },
};

// ============================================================================
// API Query
// ============================================================================

/**
 * Query options for recommendation detail
 */
function recommendationDetailQueryOptions(recommendationId: string) {
  return {
    queryKey: ["recommendation", recommendationId] as const,
    queryFn: async (): Promise<RecommendationDetailData> => {
      const response = await api.v1.recommendations[":id"].$get({
        param: { id: recommendationId },
      });
      return unwrapResponse<RecommendationDetailData>(response);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Screen header with back button and title
 */
function ScreenHeader({
  title,
  onBack,
  onRefresh,
  isRefreshing,
  exportRef,
  exportFileName,
  exportTitle,
  exportSubtitle,
}: {
  title?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  exportRef?: React.RefObject<HTMLElement | null>;
  exportFileName?: string;
  exportTitle?: string;
  exportSubtitle?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        {title && (
          <h1 className="text-lg font-semibold truncate max-w-[200px] sm:max-w-none">
            {title}
          </h1>
        )}
      </div>
      <div className="flex items-center gap-2">
        {exportRef && (
          <ExportAnalysisButton
            targetRef={exportRef}
            fileName={exportFileName}
            exportTitle={exportTitle}
            exportSubtitle={exportSubtitle}
            showLabel={false}
            showFormatOptions
            variant="ghost"
            size="icon"
          />
        )}
        {onRefresh && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="h-9 w-9"
            aria-label="Refresh data"
          >
            <RefreshCw
              className={cn("h-4 w-4", isRefreshing && "animate-spin")}
            />
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Error state display
 */
function ErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: unknown;
  onRetry: () => void;
  onBack?: () => void;
}) {
  const isNotFound =
    error instanceof Error &&
    (error.message.includes("not found") || error.message.includes("NOT_FOUND"));

  const message =
    error instanceof Error
      ? error.message
      : "An unexpected error occurred while loading recommendation details.";

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {isNotFound ? "Recommendation Not Found" : "Error Loading Recommendation"}
      </h2>
      <p className="text-muted-foreground text-center mb-6 max-w-md">{message}</p>
      <div className="flex gap-3">
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Go Back
          </Button>
        )}
        {!isNotFound && <Button onClick={onRetry}>Try Again</Button>}
      </div>
    </div>
  );
}

/**
 * Recommendation header with type badge, priority, and actions
 */
function RecommendationHeader({
  data,
  onExecute,
  onDismiss,
}: {
  data: RecommendationDetailData;
  onExecute?: () => void;
  onDismiss?: () => void;
}) {
  const typeConfig = TYPE_CONFIG[data.type];
  const priorityConfig = PRIORITY_CONFIG[data.priority];
  const TypeIcon = typeConfig.icon;

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge className={cn("gap-1.5", typeConfig.color)}>
            <TypeIcon className="h-3.5 w-3.5" />
            {typeConfig.label}
          </Badge>
          <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
        </div>

        {/* Reason */}
        <p className="text-sm text-foreground leading-relaxed mb-3">
          {data.reason}
        </p>

        {/* Impact */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 mb-4">
          <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">{data.impactDescription}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {data.type === "switch" && (
            <Button onClick={onExecute} className="gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              Execute Switch
            </Button>
          )}
          {data.replacementFund && (
            <WatchlistButton
              isin={data.replacementFund.isin}
              fundName={data.replacementFund.name}
              showLabel
              size="sm"
              variant="outline"
            />
          )}
          <Button variant="outline" onClick={onDismiss} className="gap-2">
            <X className="h-4 w-4" />
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Exit Fund Analysis Section
 */
function ExitFundSection({
  exitFund,
}: {
  exitFund: NonNullable<RecommendationDetailData["exitFund"]>;
}) {
  const ratingConfig = RATING_CONFIG[exitFund.rating];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TrendingDown className="h-4 w-4 text-amber-500" />
          Exit Fund Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fund Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{exitFund.name}</h3>
            <p className="text-sm text-muted-foreground">{exitFund.amcName}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={cn("text-xs", ratingConfig.bgColor, ratingConfig.textColor)}>
                {ratingConfig.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Rank #{exitFund.rank} of {exitFund.totalFundsInCategory}
              </span>
            </div>
          </div>
          <CopyISINButton isin={exitFund.isin} />
        </div>

        {/* Holding Details */}
        {exitFund.holding && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/50">
            <div>
              <p className="text-xs text-muted-foreground">Current Value</p>
              <p className="font-semibold">{formatINR(exitFund.holding.currentValue)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Returns</p>
              <p
                className={cn(
                  "font-semibold",
                  exitFund.holding.totalReturns >= 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-rose-600 dark:text-rose-400"
                )}
              >
                {formatINR(exitFund.holding.totalReturns)}{" "}
                <span className="text-sm font-normal">
                  ({formatPercent(exitFund.holding.totalReturnsPercent)})
                </span>
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Invested Amount</p>
              <p className="font-medium">{formatINR(exitFund.holding.investedAmount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">XIRR</p>
              <p
                className={cn(
                  "font-medium",
                  exitFund.holding.xirr >= 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-rose-600 dark:text-rose-400"
                )}
              >
                {formatPercent(exitFund.holding.xirr)}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                First investment: {formatDate(exitFund.holding.firstInvestmentDate)}
              </p>
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border/50">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">1Y Returns</p>
            <p className="font-mono text-sm font-medium">
              {formatPercent(exitFund.returns1Y, 1)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">3Y Returns</p>
            <p className="font-mono text-sm font-medium">
              {formatPercent(exitFund.returns3Y, 1)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Expense Ratio</p>
            <p className="font-mono text-sm font-medium">{exitFund.expenseRatio}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Replacement Fund Section
 */
function ReplacementFundSection({
  replacementFund,
}: {
  replacementFund: NonNullable<RecommendationDetailData["replacementFund"]>;
}) {
  const ratingConfig = RATING_CONFIG[replacementFund.rating];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          Recommended Replacement
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fund Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{replacementFund.name}</h3>
            <p className="text-sm text-muted-foreground">{replacementFund.amcName}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={cn("text-xs", ratingConfig.bgColor, ratingConfig.textColor)}>
                {ratingConfig.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Rank #{replacementFund.rank} of {replacementFund.totalFundsInCategory}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <CopyISINButton isin={replacementFund.isin} />
            <WatchlistButton
              isin={replacementFund.isin}
              fundName={replacementFund.name}
              size="icon-sm"
            />
          </div>
        </div>

        {/* Score Breakdown */}
        <ScoreBreakdownBars
          consistencyScore={replacementFund.consistencyScore}
          recencyScore={85} // Placeholder - API should provide this
          volatilityScore={65} // Placeholder - API should provide this
          showCard={false}
        />

        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/50">
          <div>
            <p className="text-xs text-muted-foreground">1Y Returns</p>
            <p className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
              {formatPercent(replacementFund.returns1Y, 1)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">3Y Returns</p>
            <p className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
              {formatPercent(replacementFund.returns3Y, 1)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Expense Ratio</p>
            <p className="font-mono font-medium">{replacementFund.expenseRatio}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Sharpe Ratio</p>
            <p className="font-mono font-medium">{replacementFund.sharpeRatio.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Alpha</p>
            <p className="font-mono font-medium">{formatPercent(replacementFund.alpha, 2)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Benchmark</p>
            <p className="text-xs truncate" title={replacementFund.benchmarkIndex}>
              {replacementFund.benchmarkIndex}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Tax Impact Summary Section
 */
function TaxImpactSection({ taxImpact }: { taxImpact: TaxImpact }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          Tax Impact Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Short-Term Gains</p>
            <p
              className={cn(
                "font-mono font-medium",
                taxImpact.shortTermGain > 0 && "text-amber-600 dark:text-amber-400"
              )}
            >
              {formatINR(taxImpact.shortTermGain)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">STCG Tax (20%)</p>
            <p className="font-mono font-medium">{formatINR(taxImpact.estimatedStcgTax)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Long-Term Gains</p>
            <p
              className={cn(
                "font-mono font-medium",
                taxImpact.longTermGain > 0 && "text-emerald-600 dark:text-emerald-400"
              )}
            >
              {formatINR(taxImpact.longTermGain)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">LTCG Tax (12.5%)</p>
            <p className="font-mono font-medium">{formatINR(taxImpact.estimatedLtcgTax)}</p>
          </div>
        </div>

        {/* LTCG Exemption Used */}
        {taxImpact.ltcgExemptionUsed > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-xs">
            <Info className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <span className="text-emerald-700 dark:text-emerald-300">
              LTCG exemption of {formatINR(taxImpact.ltcgExemptionUsed)} applied
            </span>
          </div>
        )}

        {/* Total Tax */}
        <div className="flex items-center justify-between pt-3 border-t border-border/50">
          <span className="font-medium">Total Estimated Tax</span>
          <span className="font-mono font-semibold">
            {formatINR(taxImpact.totalEstimatedTax)}
          </span>
        </div>

        {/* Net Proceeds */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <span className="font-medium">Net Proceeds</span>
          <span className="font-mono font-semibold text-lg">
            {formatINR(taxImpact.netProceeds)}
          </span>
        </div>

        {/* Tax Notes */}
        {taxImpact.taxNotes && taxImpact.taxNotes.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-1">Notes:</p>
            <ul className="text-xs text-muted-foreground space-y-0.5">
              {taxImpact.taxNotes.map((note, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-muted-foreground">-</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

/**
 * Full screen skeleton loader
 */
export function RecommendationDetailScreenSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col min-h-screen", className)}>
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4 px-4 pt-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-9 w-9 rounded-md" />
      </div>

      {/* Content skeletons */}
      <div className="px-4 py-4 space-y-4">
        {/* Header card */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex gap-2">
              <Skeleton className="h-6 w-32 rounded-full" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-12 w-full rounded-lg" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-24" />
            </div>
          </CardContent>
        </Card>

        {/* Exit fund skeleton */}
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-5 w-36" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>

        {/* Replacement fund skeleton */}
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-5 w-44" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <ScoreBreakdownBarsSkeleton showCard={false} />
          </CardContent>
        </Card>

        {/* Comparison table skeleton */}
        <FundComparisonTableSkeleton />

        {/* Switch cost skeleton */}
        <SwitchCostTableSkeleton />
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Recommendation Detail Screen
 *
 * Displays comprehensive information about a recommendation including
 * exit fund analysis, replacement fund details, comparison, and costs.
 *
 * @example
 * ```tsx
 * function RecommendationPage({ params }: { params: { recommendationId: string } }) {
 *   const navigate = useNavigate();
 *
 *   return (
 *     <RecommendationDetailScreen
 *       recommendationId={params.recommendationId}
 *       onBack={() => navigate(-1)}
 *     />
 *   );
 * }
 * ```
 */
export function RecommendationDetailScreen({
  recommendationId,
  onBack,
  className,
}: RecommendationDetailScreenProps) {
  // Ref for export content
  const exportContentRef = useRef<HTMLDivElement>(null);

  // Fetch recommendation data
  const {
    data: recommendationData,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery(recommendationDetailQueryOptions(recommendationId));

  // Action handlers
  const handleExecute = () => {
    // TODO: Implement switch execution flow
    console.log("Execute switch for recommendation:", recommendationId);
  };

  const handleDismiss = () => {
    // TODO: Implement dismiss flow
    console.log("Dismiss recommendation:", recommendationId);
    onBack?.();
  };

  // Generate export metadata
  const exportFileName = recommendationData?.exitFund
    ? `${recommendationData.exitFund.name.toLowerCase().replace(/\s+/g, "-")}-analysis`
    : "recommendation-analysis";

  const exportTitle = recommendationData?.exitFund
    ? `${TYPE_CONFIG[recommendationData.type].label}: ${recommendationData.exitFund.name}`
    : "Recommendation Analysis";

  const exportSubtitle = recommendationData?.replacementFund
    ? `Suggested switch to: ${recommendationData.replacementFund.name}`
    : undefined;

  // Handle error state
  if (isError) {
    return (
      <div className={cn("flex flex-col min-h-screen", className)}>
        <div className="px-4 pt-4">
          <ScreenHeader onBack={onBack} />
        </div>
        <ErrorState error={error} onRetry={() => refetch()} onBack={onBack} />
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return <RecommendationDetailScreenSkeleton className={className} />;
  }

  if (!recommendationData) {
    return (
      <div className={cn("flex flex-col min-h-screen", className)}>
        <div className="px-4 pt-4">
          <ScreenHeader onBack={onBack} />
        </div>
        <ErrorState
          error={new Error("Recommendation data not available")}
          onRetry={() => refetch()}
          onBack={onBack}
        />
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col min-h-screen bg-background", className)}>
      {/* Screen Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b px-4 py-2">
        <ScreenHeader
          title="Recommendation Details"
          onBack={onBack}
          onRefresh={() => refetch()}
          isRefreshing={isRefetching}
          exportRef={exportContentRef}
          exportFileName={exportFileName}
          exportTitle={exportTitle}
          exportSubtitle={exportSubtitle}
        />
      </div>

      {/* Main Content */}
      <div ref={exportContentRef} className="flex-1 px-4 py-4 space-y-4">
        {/* Recommendation Header */}
        <RecommendationHeader
          data={recommendationData}
          onExecute={handleExecute}
          onDismiss={handleDismiss}
        />

        {/* Exit Fund Analysis */}
        {recommendationData.exitFund && (
          <ExitFundSection exitFund={recommendationData.exitFund} />
        )}

        {/* Replacement Fund */}
        {recommendationData.replacementFund && (
          <ReplacementFundSection replacementFund={recommendationData.replacementFund} />
        )}

        {/* Side-by-Side Comparison */}
        {recommendationData.exitFund && recommendationData.replacementFund && (
          <FundComparisonTable
            exitFund={{
              name: recommendationData.exitFund.name,
              rating: recommendationData.exitFund.rating,
              rank: recommendationData.exitFund.rank,
              totalFundsInCategory: recommendationData.exitFund.totalFundsInCategory,
              returns1Y: recommendationData.exitFund.returns1Y,
              returns3Y: recommendationData.exitFund.returns3Y,
              expenseRatio: recommendationData.exitFund.expenseRatio,
              consistencyScore: recommendationData.exitFund.consistencyScore,
            }}
            replacementFund={{
              name: recommendationData.replacementFund.name,
              rating: recommendationData.replacementFund.rating,
              rank: recommendationData.replacementFund.rank,
              totalFundsInCategory: recommendationData.replacementFund.totalFundsInCategory,
              returns1Y: recommendationData.replacementFund.returns1Y,
              returns3Y: recommendationData.replacementFund.returns3Y,
              expenseRatio: recommendationData.replacementFund.expenseRatio,
              consistencyScore: recommendationData.replacementFund.consistencyScore,
            }}
            comparison={recommendationData.comparison}
          />
        )}

        {/* Switch Cost Breakdown */}
        {recommendationData.switchCost && (
          <SwitchCostTable
            data={{
              exitLoad: recommendationData.switchCost.exitLoad,
              stt: recommendationData.switchCost.stt,
              stampDuty: recommendationData.switchCost.stampDuty,
              stcgTax: recommendationData.taxImpact?.estimatedStcgTax,
              ltcgTax: recommendationData.taxImpact?.estimatedLtcgTax,
              totalCost: recommendationData.switchCost.totalCost +
                (recommendationData.taxImpact?.totalEstimatedTax ?? 0),
              costPercentage: recommendationData.switchCost.costPercentage,
              notes: recommendationData.switchCost.notes,
            }}
            redemptionValue={recommendationData.exitFund?.holding?.currentValue}
          />
        )}

        {/* Tax Impact Summary */}
        {recommendationData.taxImpact && (
          <TaxImpactSection taxImpact={recommendationData.taxImpact} />
        )}
      </div>
    </div>
  );
}

export default RecommendationDetailScreen;
